import {
  AfterContentChecked,
  Component,
  OnDestroy,
  signal,
  ViewChild,
} from "@angular/core";
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterEvent,
  RouterOutlet,
} from "@angular/router";
import { Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { NbLayoutComponent, NbLayoutModule } from "@nebular/theme";
import { Store } from "@ngrx/store";
import { AppState } from "./store/reducers";
import { ResizableModule, ResizeEvent } from "angular-resizable-element";
import { Actions } from "@ngrx/effects";
import { AsyncPipe, NgOptimizedImage } from "@angular/common";
import { HeaderComponent } from "./modules/components/header/header.component";
import { TimeSelectorComponent } from "./modules/components/time-selector/time-selector.component";
import { AssetTreeSidebarComponent } from "./modules/components/asset-tree-sidebar/asset-tree-sidebar.component";
import { TopStatusBarComponent } from "./modules/components/top-status-bar/top-status-bar.component";
import { ProgressBar } from "primeng/progressbar";
import { AuthService } from "./auth/services/auth.service";

@Component({
  selector: "ngx-app",
  styleUrls: ["./app.component.scss"],
  templateUrl: "app.component.html",
  imports: [
    NbLayoutModule,
    ResizableModule,
    NgOptimizedImage,
    HeaderComponent,
    RouterOutlet,
    AsyncPipe,
    TimeSelectorComponent,
    AssetTreeSidebarComponent,
    TopStatusBarComponent,
    ProgressBar,
  ],
})
export class AppComponent implements OnDestroy, AfterContentChecked {
  public loading$: Observable<boolean>;

  private readonly _maxWidth = 400;
  private readonly _minWidth = 220;
  private readonly startingSidebarWidth = 292;
  public currentSidebarWidth: number = this.startingSidebarWidth;
  public savedWidth: number = this.currentSidebarWidth;

  private destroyed$ = new Subject<void>();
  public resizing = false;
  private resizeStartLocation: number = 0;
  public showSideBar$: Observable<boolean>;

  @ViewChild("nbLayout") nbLayout: NbLayoutComponent;

  readonly showGlobalProgressBar = signal(false);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private updates$: Actions,
    private store: Store<AppState>,
    readonly authService: AuthService,
  ) {
    this.loading$ = router.events.pipe(
      map((event) => this.navigationInterceptor(event)),
    );
    this.showSideBar$ = this.store.select(
      (state: AppState) => state.dashboard.showSideMenu,
    );
    this.showSideBar$.pipe(takeUntil(this.destroyed$)).subscribe((show) => {
      this.currentSidebarWidth = show ? this.savedWidth : 0;
    });

    this.router.events
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: RouterEvent | any) => {
        if (event instanceof NavigationStart) {
          this.showGlobalProgressBar.set(true);
        }

        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationSkipped ||
          event instanceof NavigationError
        ) {
          this.showGlobalProgressBar.set(false);
        }
      });
  }

  private navigationInterceptor(event): boolean {
    if (event instanceof NavigationStart) {
      return true;
    }
    if (event instanceof NavigationEnd) {
      return false;
    }
    if (event instanceof NavigationSkipped) {
      return false;
    }
    if (event instanceof NavigationCancel) {
      return false;
    }
    if (event instanceof NavigationError) {
      return false;
    }
    return true;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onResizeEnd(event: ResizeEvent): void {
    this.resizing = false;
    this.savedWidth = this.currentSidebarWidth;
  }

  public validate(resizeEvent: ResizeEvent): boolean {
    return (
      resizeEvent.rectangle.right >= this._minWidth &&
      resizeEvent.rectangle.right <= this._maxWidth
    );
  }

  onResizeStart(event: ResizeEvent) {
    this.resizing = true;
    this.resizeStartLocation = event.rectangle.right;
  }

  onResizing(event: MouseEvent) {
    if (this.resizing) {
      this.currentSidebarWidth = event.clientX;
    }
  }

  ngAfterContentChecked(): void {
    if (this.nbLayout) {
      // @ts-ignore
      this.nbLayout.registerAsOverlayContainer();
    }
  }
}
